import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  CircularProgress,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardMedia,
  CardActions,
  Chip,
  FormHelperText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const CompanyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [previewDialog, setPreviewDialog] = useState({ open: false, image: null, title: '' });
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  
  // Get current date for default values
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getDatePlusOneYear = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    companyName: '',
    companyNameArabic: '',
    companyNameLocal: '',
    phoneNo: '',
    mobileNo: '',
    eMail: '',
    country: '',
    city: '',
    subcity: '',
    wereda: '',
    address: '',
    fax: '',
    poBox: '',
    generalManager: '',
    generalManagerLocal: '',
    licenseNo: '',
    agencyLicenseNo: '',
    licenseDate: getDatePlusOneYear(), // Set to current date + 1 year
    registrationNo: '',
    registrationDate: getCurrentDate(), // Set to current date
    companyType: '',
    is_Active: true,
    currency: '',
    allowEdit: false,
    moveApplicants: false,
    isPremium: false,
    commission: '',
    videoExpDate: null, // Set to null
  });

  // Image state
  const [images, setImages] = useState({
    header: { file: null, preview: null, existing: null, base64: null },
    sharedHeader: { file: null, preview: null, existing: null, base64: null },
    footer: { file: null, preview: null, existing: null, base64: null },
    stikerLogo: { file: null, preview: null, existing: null, base64: null },
    stamp: { file: null, preview: null, existing: null, base64: null },
    signature: { file: null, preview: null, existing: null, base64: null },
  });

  const fileInputRefs = {
    header: useRef(null),
    sharedHeader: useRef(null),
    footer: useRef(null),
    stikerLogo: useRef(null),
    stamp: useRef(null),
    signature: useRef(null),
  };

  const isEdit = !!id;

  // Company type options
  const companyTypes = [
    'Agency',
    'Agent',
    'Embassy',
    'Foreign Agent',
    'Foreign Company',
    'Ticket Office',
    'Proprietor',
    'Staff'
  ];

  // Country options
  const countries = [
    'SAUDI ARABIA',
    'QATAR',
    'KUWAIT',
    'UAE',
    'BAHRAIN',
    'JORDAN'
  ];

  // Currency options
  const currencies = [
    { code: 'SR', label: 'Saudi Riyal' },
    { code: 'QR', label: 'Qatari Riyal' },
    { code: 'KWD', label: 'Kuwaiti Dinar' },
    { code: 'AED', label: 'UAE Dirham' },
    { code: 'BHD', label: 'Bahraini Dinar' },
    { code: 'JOD', label: 'Jordanian Dinar' },
    { code: 'USD', label: 'US Dollar' }
  ];

  // Required fields
  const requiredFields = {
    companyName: 'Company Name is required',
    eMail: 'Email is required',
    registrationDate: 'Registration Date is required',
    companyType: 'Company Type is required',
    licenseDate: 'License Date is required',
  };

  useEffect(() => {
    if (isEdit) {
      fetchCompany();
    }
  }, [id]);

  const fetchCompany = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(`/Companies/${id}`);
      const data = response.data;
      
      setFormData({
        ...data,
        licenseDate: data.licenseDate ? new Date(data.licenseDate).toISOString().split('T')[0] : getDatePlusOneYear(),
        registrationDate: data.registrationDate ? new Date(data.registrationDate).toISOString().split('T')[0] : getCurrentDate(),
        videoExpDate: data.videoExpDate ? new Date(data.videoExpDate).toISOString().split('T')[0] : null,
        commission: data.commission || '',
      });

      // Set existing images
      const imageFields = ['header', 'sharedHeader', 'footer', 'stikerLogo', 'stamp', 'signature'];
      imageFields.forEach(field => {
        if (data[field]) {
          let imageUrl;
          if (typeof data[field] === 'string' && data[field].startsWith('/9j/')) {
            imageUrl = `data:image/jpeg;base64,${data[field]}`;
          } else if (typeof data[field] === 'string' && data[field].startsWith('data:image')) {
            imageUrl = data[field];
          } else {
            imageUrl = `data:image/png;base64,${data[field]}`;
          }
          
          setImages(prev => ({
            ...prev,
            [field]: { ...prev[field], existing: imageUrl }
          }));
        }
      });
    } catch (error) {
      console.error('Error fetching company:', error);
      setError('Failed to load company data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const validateField = (field, value) => {
    if (requiredFields[field] && !value) {
      return requiredFields[field];
    }
    // Email validation
    if (field === 'eMail' && value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
      return 'Invalid email format';
    }
    return null;
  };

  const validateForm = () => {
    const errors = {};
    const touched = {};
    let hasErrors = false;

    // Mark all required fields as touched
    Object.keys(requiredFields).forEach(field => {
      touched[field] = true;
      const error = validateField(field, formData[field]);
      if (error) {
        errors[field] = error;
        hasErrors = true;
      }
    });

    setValidationErrors(errors);
    setTouchedFields(touched);
    return !hasErrors;
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleBlur = (field) => (event) => {
    setTouchedFields(prev => ({
      ...prev,
      [field]: true
    }));
    
    // Validate on blur
    const error = validateField(field, formData[field]);
    if (error) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleFileChange = (field) => (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1];
        setImages(prev => ({
          ...prev,
          [field]: {
            file: file,
            preview: reader.result,
            existing: null,
            base64: base64String
          }
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = (field) => {
    setImages(prev => ({
      ...prev,
      [field]: {
        file: null,
        preview: null,
        existing: prev[field].existing,
        base64: null
      }
    }));
    if (fileInputRefs[field].current) {
      fileInputRefs[field].current.value = '';
    }
  };

  const handleRemoveExistingImage = (field) => {
    setImages(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        existing: null,
        deleteExisting: true
      }
    }));
  };

  const handlePreviewImage = (imageUrl, title) => {
    setPreviewDialog({ open: true, image: imageUrl, title });
  };

  const handleClosePreview = () => {
    setPreviewDialog({ open: false, image: null, title: '' });
  };

  const handleSubmit = async () => {
    // Validate all fields and mark them as touched
    const isValid = validateForm();
    
    if (!isValid) {
      // Scroll to the first error field
      const firstErrorField = Object.keys(validationErrors)[0];
      if (firstErrorField) {
        const element = document.getElementById(`field-${firstErrorField}`);
        if (element) {
          // Expand the accordion if it's collapsed
          const accordion = element.closest('.MuiAccordion-root');
          if (accordion) {
            const isExpanded = accordion.getAttribute('aria-expanded') === 'true';
            if (!isExpanded) {
              const summary = accordion.querySelector('.MuiAccordionSummary-root');
              if (summary) {
                summary.click();
              }
            }
          }
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }, 300);
        }
      }
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const submitData = {
        ...formData,
        licenseDate: formData.licenseDate || null,
        registrationDate: formData.registrationDate || null,
        videoExpDate: formData.videoExpDate || null,
        commission: formData.commission ? parseFloat(formData.commission) : null,
      };

      const imageFields = ['header', 'sharedHeader', 'footer', 'stikerLogo', 'stamp', 'signature'];
      imageFields.forEach(field => {
        if (images[field].base64) {
          submitData[field] = images[field].base64;
        } else if (images[field].deleteExisting) {
          submitData[field] = null;
        }
      });

      if (isEdit) {
        await api.put(`/Companies/${id}`, submitData, {
          headers: { 'Content-Type': 'application/json' }
        });
      } else {
        await api.post('/Companies', submitData, {
          headers: { 'Content-Type': 'application/json' }
        });
      }

      navigate('/companies/companylist');
    } catch (error) {
      console.error('Error saving company:', error);
      if (error.response) {
        setError(`Failed to save company: ${error.response.data || error.response.statusText}`);
      } else {
        setError('Failed to save company. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    Object.values(images).forEach(img => {
      if (img.preview && img.preview.startsWith('blob:')) {
        URL.revokeObjectURL(img.preview);
      }
    });
    navigate('/companies/companylist');
  };

  const renderImageUpload = (field, label) => {
    const imageData = images[field];
    const displayImage = imageData.preview || imageData.existing;
    const hasImage = !!displayImage;

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {hasImage ? (
            <Card sx={{ width: '100%', maxWidth: 300, mb: 1, position: 'relative' }}>
              <CardMedia
                component="img"
                height="150"
                image={displayImage}
                alt={label}
                sx={{ objectFit: 'contain', bgcolor: '#f5f5f5' }}
              />
              <CardActions sx={{ justifyContent: 'center' }}>
                <Button
                  size="small"
                  startIcon={<VisibilityIcon />}
                  onClick={() => handlePreviewImage(displayImage, label)}
                >
                  Preview
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    if (imageData.preview) {
                      handleRemoveImage(field);
                    } else if (imageData.existing) {
                      handleRemoveExistingImage(field);
                    }
                  }}
                >
                  Remove
                </Button>
              </CardActions>
              {imageData.preview && (
                <Chip 
                  label="New" 
                  color="primary" 
                  size="small" 
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                />
              )}
            </Card>
          ) : (
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ py: 3, mb: 1 }}
              startIcon={<CloudUploadIcon />}
            >
              Upload {label}
              <input
                type="file"
                accept="image/*"
                hidden
                ref={fileInputRefs[field]}
                onChange={handleFileChange(field)}
              />
            </Button>
          )}
          {!hasImage && (
            <Typography variant="caption" color="textSecondary">
              No image uploaded
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxHeight: 'calc(100vh - 100px)', overflow: 'auto' }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1">
            {isEdit ? 'Edit Company' : 'Create New Company'}
          </Typography>
          <Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={isSaving}
              sx={{ mr: 1 }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Basic Information */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Basic Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  id="field-companyName"
                  fullWidth
                  label="Company Name"
                  value={formData.companyName || ''}
                  onChange={handleChange('companyName')}
                  onBlur={handleBlur('companyName')}
                  required
                  error={!!validationErrors.companyName && touchedFields.companyName}
                  helperText={touchedFields.companyName && validationErrors.companyName}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-error': {
                        '& fieldset': {
                          borderColor: '#d32f2f',
                        },
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name (Arabic)"
                  value={formData.companyNameArabic || ''}
                  onChange={handleChange('companyNameArabic')}
                  onBlur={handleBlur('companyNameArabic')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name (Local)"
                  value={formData.companyNameLocal || ''}
                  onChange={handleChange('companyNameLocal')}
                  onBlur={handleBlur('companyNameLocal')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl 
                  fullWidth 
                  required
                  error={!!validationErrors.companyType && touchedFields.companyType}
                >
                  <InputLabel>Company Type</InputLabel>
                  <Select
                    id="field-companyType"
                    value={formData.companyType || ''}
                    onChange={handleChange('companyType')}
                    onBlur={handleBlur('companyType')}
                    label="Company Type"
                    sx={{
                      '&.Mui-error': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#d32f2f',
                        },
                      },
                    }}
                  >
                    {companyTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                  {touchedFields.companyType && validationErrors.companyType && (
                    <FormHelperText error>{validationErrors.companyType}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  id="field-eMail"
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.eMail || ''}
                  onChange={handleChange('eMail')}
                  onBlur={handleBlur('eMail')}
                  required
                  error={!!validationErrors.eMail && touchedFields.eMail}
                  helperText={touchedFields.eMail && validationErrors.eMail}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-error': {
                        '& fieldset': {
                          borderColor: '#d32f2f',
                        },
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone No."
                  value={formData.phoneNo || ''}
                  onChange={handleChange('phoneNo')}
                  onBlur={handleBlur('phoneNo')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Mobile No."
                  value={formData.mobileNo || ''}
                  onChange={handleChange('mobileNo')}
                  onBlur={handleBlur('mobileNo')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Fax"
                  value={formData.fax || ''}
                  onChange={handleChange('fax')}
                  onBlur={handleBlur('fax')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="P.O. Box"
                  value={formData.poBox || ''}
                  onChange={handleChange('poBox')}
                  onBlur={handleBlur('poBox')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={formData.currency || ''}
                    onChange={handleChange('currency')}
                    onBlur={handleBlur('currency')}
                    label="Currency"
                  >
                    {currencies.map((curr) => (
                      <MenuItem key={curr.code} value={curr.code}>
                        {curr.code} - {curr.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Address Information */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Address Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={formData.country || ''}
                    onChange={handleChange('country')}
                    onBlur={handleBlur('country')}
                    label="Country"
                  >
                    {countries.map((country) => (
                      <MenuItem key={country} value={country}>
                        {country}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city || ''}
                  onChange={handleChange('city')}
                  onBlur={handleBlur('city')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Subcity"
                  value={formData.subcity || ''}
                  onChange={handleChange('subcity')}
                  onBlur={handleBlur('subcity')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Wereda"
                  value={formData.wereda || ''}
                  onChange={handleChange('wereda')}
                  onBlur={handleBlur('wereda')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={2}
                  value={formData.address || ''}
                  onChange={handleChange('address')}
                  onBlur={handleBlur('address')}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* License & Registration */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">License & Registration</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="License No."
                  value={formData.licenseNo || ''}
                  onChange={handleChange('licenseNo')}
                  onBlur={handleBlur('licenseNo')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="MOLSA No."
                  value={formData.agencyLicenseNo || ''}
                  onChange={handleChange('agencyLicenseNo')}
                  onBlur={handleBlur('agencyLicenseNo')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  id="field-licenseDate"
                  fullWidth
                  label="License Date"
                  type="date"
                  value={formData.licenseDate}
                  onChange={handleChange('licenseDate')}
                  onBlur={handleBlur('licenseDate')}
                  InputLabelProps={{ shrink: true }}
                  required
                  error={!!validationErrors.licenseDate && touchedFields.licenseDate}
                  helperText={touchedFields.licenseDate && validationErrors.licenseDate}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-error': {
                        '& fieldset': {
                          borderColor: '#d32f2f',
                        },
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Registration No."
                  value={formData.registrationNo || ''}
                  onChange={handleChange('registrationNo')}
                  onBlur={handleBlur('registrationNo')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  id="field-registrationDate"
                  fullWidth
                  label="Registration Date"
                  type="date"
                  value={formData.registrationDate}
                  onChange={handleChange('registrationDate')}
                  onBlur={handleBlur('registrationDate')}
                  InputLabelProps={{ shrink: true }}
                  required
                  error={!!validationErrors.registrationDate && touchedFields.registrationDate}
                  helperText={touchedFields.registrationDate && validationErrors.registrationDate}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-error': {
                        '& fieldset': {
                          borderColor: '#d32f2f',
                        },
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Video Expiry Date"
                  type="date"
                  value={formData.videoExpDate || ''}
                  onChange={handleChange('videoExpDate')}
                  onBlur={handleBlur('videoExpDate')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Management & Settings */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Management & Settings</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="General Manager"
                  value={formData.generalManager || ''}
                  onChange={handleChange('generalManager')}
                  onBlur={handleBlur('generalManager')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="General Manager (Local)"
                  value={formData.generalManagerLocal || ''}
                  onChange={handleChange('generalManagerLocal')}
                  onBlur={handleBlur('generalManagerLocal')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Commission"
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  value={formData.commission || ''}
                  onChange={handleChange('commission')}
                  onBlur={handleBlur('commission')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_Active}
                      onChange={handleChange('is_Active')}
                      color="primary"
                    />
                  }
                  label="Active"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowEdit}
                      onChange={handleChange('allowEdit')}
                      color="primary"
                    />
                  }
                  label="Allow Edit"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.moveApplicants}
                      onChange={handleChange('moveApplicants')}
                      color="primary"
                    />
                  }
                  label="Move Applicants"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isPremium}
                      onChange={handleChange('isPremium')}
                      color="primary"
                    />
                  }
                  label="Premium"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Images & Documents */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Images & Documents</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {renderImageUpload('header', 'Header Image')}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderImageUpload('sharedHeader', 'Shared Header')}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderImageUpload('footer', 'Footer Image')}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderImageUpload('stikerLogo', 'Sticker Logo')}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderImageUpload('stamp', 'Stamp Image')}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderImageUpload('signature', 'Signature Image')}
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Action Buttons at bottom */}
        <Box display="flex" justifyContent="center" gap={2} mt={3} pt={2} borderTop={1} borderColor="divider">
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : isEdit ? 'Update Company' : 'Create Company'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="large"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </Box>
      </Paper>

      {/* Image Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {previewDialog.title}
          <IconButton
            aria-label="close"
            onClick={handleClosePreview}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CancelIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          {previewDialog.image && (
            <img
              src={previewDialog.image}
              alt={previewDialog.title}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyForm;